/**
 * AvenCart - one cart-write path for every custom section in this theme,
 * plus a bridge so third-party bundle apps and the theme's own quantity
 * selector stop fighting over the product form.
 *
 * Why this file exists
 * --------------------
 * 1. The custom ERLY sections (bundle offers, sticky ATC, PDP upsells) used to
 *    POST straight to /cart/add.js. That adds the line server-side but never
 *    re-renders the cart drawer or the header count, so the UI goes stale until
 *    the shopper reloads. AvenCart.add() does what Dawn's own product-form.js
 *    does: it asks the server to re-render the cart sections alongside the add,
 *    publishes Dawn's cart-update event, then hands the payload to the cart
 *    element's renderContents().
 *
 * 2. Bundle apps (Adoric, and most others) inject a second hidden
 *    <input name="quantity"> into the product form when a tier is selected.
 *    With two fields of the same name the app's value silently wins and the
 *    visible quantity stepper does nothing. bridgeQuantity() keeps the two in
 *    sync and makes sure only one is ever submitted.
 *
 * Section contract - nothing here is tied to a specific bundler:
 *   [data-aven-add]   click adds to cart. Reads data-variant-id and data-qty.
 *   [data-bundle-qty] click selects a tier and pushes the quantity into the
 *                     PDP quantity input, so the main Add to cart carries it.
 * Both also fire the document-level 'aven:bundle-select' event.
 */
(function () {
  if (window.AvenCart) return;

  var PRODUCT_FORM = 'form[data-type="add-to-cart-form"]';
  var VISIBLE_QTY = '.ph__qty-input';

  function route(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /**
   * Add a line to the cart and refresh every cart surface on the page.
   * item:    { id, quantity, selling_plan, properties }
   * options: { open, redirect, source, trigger }
   */
  function add(item, options) {
    var opts = options || {};
    var cart = cartElement();
    var body = new FormData();

    body.append('id', item.id);
    body.append('quantity', item.quantity == null ? 1 : item.quantity);
    if (item.selling_plan) body.append('selling_plan', item.selling_plan);
    if (item.properties) {
      Object.keys(item.properties).forEach(function (key) {
        body.append('properties[' + key + ']', item.properties[key]);
      });
    }

    // Ask for the cart sections back in the same round trip. Without this the
    // drawer and the header bubble never learn that the cart changed.
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append(
        'sections',
        cart.getSectionsToRender().map(function (section) { return section.id; }).join(',')
      );
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(opts.trigger || document.activeElement);
      }
    }

    return fetch(route('cart_add_url', '/cart/add') + '.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: opts.source || 'aven-cart',
            productVariantId: String(item.id),
            cartData: data
          });
        }

        if (!cart) {
          if (opts.redirect !== false) window.location = route('cart_url', '/cart');
          return data;
        }

        cart.classList.remove('is-empty');
        if (opts.open !== false && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
        }
        return data;
      });
  }

  /** Add, then go straight to checkout. Used by the PDP Buy it now button. */
  function checkout(item, options) {
    var opts = options || {};
    opts.open = false;
    opts.redirect = false;
    return add(item, opts).then(function (data) {
      window.location = '/checkout';
      return data;
    });
  }

  function pdp() {
    return window.AvenPDP || null;
  }

  function selectBundle(detail) {
    document.dispatchEvent(new CustomEvent('aven:bundle-select', { detail: detail, bubbles: true }));
  }

  function busy(button, run) {
    if (!button || button.disabled) return;
    var original = button.innerHTML;
    button.disabled = true;
    button.textContent = '...';
    run()
      .then(function () {
        button.textContent = 'Added';
        setTimeout(function () {
          button.innerHTML = original;
          button.disabled = false;
        }, 1800);
      })
      .catch(function () {
        button.innerHTML = original;
        button.disabled = false;
      });
  }

  function markSelected(tier) {
    var scope = tier.closest('[data-bundle-group]') || document;
    scope.querySelectorAll('[data-bundle-qty]').forEach(function (el) {
      var on = el === tier;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  // ---- Generic, section-agnostic wiring -----------------------------------

  document.addEventListener('click', function (event) {
    var addEl = event.target.closest('[data-aven-add]');
    if (addEl) {
      event.preventDefault();
      var context = pdp();
      var variantId = addEl.dataset.variantId || (context && context.getVariantId());
      if (!variantId) return;
      busy(addEl, function () {
        return add(
          { id: Number(variantId), quantity: Number(addEl.dataset.qty) || 1 },
          { source: addEl.dataset.avenSource || 'aven-cart', trigger: addEl }
        );
      });
      return;
    }

    var tier = event.target.closest('[data-bundle-qty]');
    if (!tier) return;

    var quantity = Number(tier.dataset.bundleQty) || 1;
    var detail = { quantity: quantity, variantId: tier.dataset.bundleVariant || null, source: tier };
    var target = pdp();

    // Preferred path: drive the real product form so one Add to cart click
    // carries the bundle quantity. Only fall back to a direct add when the
    // page has no quantity input to drive.
    if (target && typeof target.setQuantity === 'function' && target.setQuantity(quantity)) {
      selectBundle(detail);
      markSelected(tier);
      if (typeof target.focusAtc === 'function') target.focusAtc();
      return;
    }

    selectBundle(detail);
    markSelected(tier);
    var fallbackId = tier.dataset.bundleVariant || (target && target.getVariantId());
    if (!fallbackId) return;
    add({ id: Number(fallbackId), quantity: quantity }, { source: 'aven-bundle', trigger: tier });
  });

  // Keyboard parity for bundle tiers rendered as non-button elements.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var tier = event.target.closest('[data-bundle-qty]');
    if (!tier || tier.tagName === 'BUTTON') return;
    event.preventDefault();
    tier.click();
  });

  // ---- Bundle-app quantity bridge -----------------------------------------
  //
  // Bundle apps append their own hidden <input name="quantity"> to the product
  // form. Two same-named fields means the browser submits both and the server
  // takes the last one, so the visible stepper is ignored. We:
  //   app -> theme : when the app's hidden value changes, move the stepper.
  //   theme -> app : when the stepper moves, write the app's value to match.
  //   on submit    : temporarily un-name the app's copies so exactly one
  //                  quantity is sent, then restore them.
  // Written against the pattern, not against Adoric specifically, so swapping
  // bundle apps keeps working.

  function appQuantityInputs(form, visible) {
    return Array.prototype.filter.call(
      form.querySelectorAll('[name="quantity"]'),
      function (el) { return el !== visible; }
    );
  }

  function bridgeQuantity() {
    var form = document.querySelector(PRODUCT_FORM);
    if (!form) return;
    var visible = form.querySelector(VISIBLE_QTY);
    if (!visible) return;

    var lastAppValue = null;

    function pushToApp() {
      appQuantityInputs(form, visible).forEach(function (el) { el.value = visible.value; });
      lastAppValue = visible.value;
    }

    visible.addEventListener('change', pushToApp);
    visible.addEventListener('input', pushToApp);

    // Apps set .value as a JS property, which no MutationObserver reports, so
    // a light poll is the only reliable read. 400ms is imperceptible and the
    // work is a single querySelectorAll.
    setInterval(function () {
      var inputs = appQuantityInputs(form, visible);
      if (!inputs.length) return;
      var appValue = inputs[0].value;
      if (appValue === lastAppValue || appValue === visible.value) return;
      lastAppValue = appValue;
      var context = pdp();
      if (context && typeof context.setQuantity === 'function') context.setQuantity(appValue);
      else visible.value = appValue;
    }, 400);

    // Capture phase: runs before product-form.js reads FormData off the form.
    document.addEventListener(
      'submit',
      function (event) {
        var target = event.target;
        if (!target || !target.matches || !target.matches(PRODUCT_FORM)) return;
        var suppressed = appQuantityInputs(target, visible);
        suppressed.forEach(function (el) {
          el.value = visible.value;
          el.setAttribute('data-aven-suppressed-name', el.name);
          el.removeAttribute('name');
        });
        // product-form.js builds its FormData synchronously in its own handler,
        // so the next macrotask is safe for handing the fields back to the app.
        setTimeout(function () {
          suppressed.forEach(function (el) {
            var name = el.getAttribute('data-aven-suppressed-name');
            if (!name) return;
            el.setAttribute('name', name);
            el.removeAttribute('data-aven-suppressed-name');
          });
        }, 0);
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bridgeQuantity, { once: true });
  } else {
    bridgeQuantity();
  }

  window.AvenCart = {
    add: add,
    checkout: checkout,
    cartElement: cartElement,
    selectBundle: selectBundle
  };
})();
